import type { CSSProperties, FC, ReactNode } from 'react'
import type { Tone } from '@/types'

import { cn } from '@/lib/utils'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

interface Reason {
    title: string
    text: string
}

interface ReasonTriadProps {
    index?: string
    eyebrow?: string
    title: string
    intro?: string
    reasons: Reason[]
    tone?: Tone
    id?: string
}

const DARK: Tone[] = ['night', 'deep', 'raised']

/** Three reasons, each opened by an oversized hairline numeral. */
export const ReasonTriad: FC<ReasonTriadProps> = ({
    index,
    eyebrow = 'Derfor velger håndverkere Svenn',
    title,
    intro,
    reasons,
    tone = 'paper',
    id = 'derfor'
}): ReactNode => (
    <section
        id={id}
        className={cn(`tone-${tone}`, DARK.includes(tone) && 'grain', 'relative py-24 md:py-36')}
        data-header={DARK.includes(tone) ? 'dark' : 'light'}
    >
        <div className='wrap'>
            <div className='grid grid-cols-12 gap-x-6 gap-y-8'>
                <div className='col-span-12 lg:col-span-7'>
                    <SectionLabel index={index}>{eyebrow}</SectionLabel>
                    <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                        <SplitWords text={title} />
                    </h2>
                </div>

                {intro && (
                    <p className='col-span-12 self-end text-tone-muted t-lead pretty lg:col-span-4 lg:col-start-9'>{intro}</p>
                )}
            </div>

            <ol className='mt-16 grid grid-cols-1 gap-x-6 md:mt-24 md:grid-cols-3'>
                {reasons.map((reason, i) => (
                    <li
                        key={reason.title}
                        className='reason relative border-t border-tone-line pt-8 pb-12 md:pr-6'
                        data-reveal='rise'
                        style={{ '--d': i } as CSSProperties}
                    >
                        <span className='reason-number' aria-hidden='true'>{i + 1}</span>
                        <h3 className='t-h3 relative mt-24 max-w-[16ch] balance md:mt-36'>{reason.title}</h3>
                        <p className='relative mt-4 max-w-sm text-tone-muted pretty'>{reason.text}</p>
                    </li>
                ))}
            </ol>
        </div>
    </section>
)
